/**
 * StaffDashboard — the landing surface for staff and admins after sign-in.
 *
 * Replaces the old behaviour of dropping straight into the calculator with
 * the last quote rehydrated (a documented magnet for editing the wrong
 * quote). Instead this is a focused worklist: quotes still in progress,
 * active orders, and — the collections view — completed orders that
 * still owe money. An in-progress calculator workspace is surfaced here as
 * an explicit, opt-in "Resume" card rather than the default screen.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OrderStatusBadge from '../modules/orders/components/OrderStatusBadge';
import { Button, Card, KpiCard, SectionHeader } from '../components/ui';
import { useAuthStore } from '../store/useAuthStore';
import { useQuoteStore } from '../store/useQuoteStore';
import {
  fetchAllQuotations,
  type QuoteListLike,
  type QuoteSummary,
} from '../services/quoteService';
import { listOrders } from '../modules/orders/services/orderEntityService';
import { buildDashboard, type CompletedBalance, type DashboardData } from '../utils/dashboard';
import { listCallbackRequests } from '../modules/measurement/services/measurementService';
import { listWorkOrders, isOpenWorkOrder } from '../modules/workOrders/services/workOrderService';
import { listRequisitions } from '../modules/requisitions/services/requisitionService';
import { formatSgd } from '../../shared/money';
import { displayOrderNumber } from '../utils/orderNumber';
import { buildAcceptedQuoteRows, type AcceptedQuoteRow } from '../utils/acceptedQuoteRows';
import type { Order, MeasurementConfirmation } from '../types';

const QUOTE_STATUS_META: Record<string, { label: string; dot: string; text: string }> = {
  DRAFT: { label: 'Draft', dot: 'bg-outline', text: 'text-outline' },
  SENT: { label: 'Sent', dot: 'bg-blue-500', text: 'text-blue-700' },
  CLIENT_CONFIRMED: { label: 'Confirmed', dot: 'bg-emerald-500', text: 'text-emerald-700' },
};

/** Whole days since an ISO timestamp; null if unparseable. */
const daysSince = (value: unknown): number | null => {
  if (!value || typeof value !== 'string') return null;
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
};
const ageLabel = (d: number | null): string =>
  d === null ? '' : d <= 0 ? 'today' : d === 1 ? '1 day ago' : `${d} days ago`;
const STALE_DAYS = 14;

const ACCEPTED_SIGNAL_LABEL: Record<AcceptedQuoteRow['signal'], string> = {
  ACCEPTED: 'Accepted · awaiting deposit',
  UPDATE_REQUESTED: 'Requested a fresh quotation',
  BOTH: 'Accepted · also requested a fresh quotation',
};

export default function StaffDashboard() {
  const { user, role, profile } = useAuthStore();
  const navigate = useNavigate();
  // Lower-cased: quote/order authorship fields are stored lower-case (the
  // rules compare against emailLower()), and the own-quotes query is exact.
  const email = (user?.email ?? '').toLowerCase();

  // In-progress calculator workspace (persisted) — surfaced as an explicit
  // resume card instead of being auto-loaded on landing.
  const wsCustomerName = useQuoteStore((s) => s.customer.name);
  const wsItemCount = useQuoteStore((s) => s.items.length);
  const hasWorkspace = wsItemCount > 0 || wsCustomerName.trim().length > 0;
  const [workspaceDismissed, setWorkspaceDismissed] = useState(false);

  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [callbackMcs, setCallbackMcs] = useState<MeasurementConfirmation[]>([]);
  // Clock for the follow-up card — read in an effect (impure Date.now() can't
  // run in render; same pattern as the quotes funnel).
  const [nowMs, setNowMs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!email) return;
    // Baseline STAFF (ADR 0005 Phase 0b) has no quote / order authorship
    // and is path-fenced out of those collections in firestore.rules. The
    // dashboard renders a stripped "claims-coming" layout for them instead
    // of attempting fetches that would 403.
    if (role === 'STAFF') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      // "My work" for every role: own-authored quotes only, even for the
      // cross-rep roles. Scoping lives in the option, not a caller-side role
      // trick (Lesson #75).
      fetchAllQuotations(email, role, { scope: 'own' }),
      listOrders({ assignedTo: email }),
      listCallbackRequests(),
    ])
      .then(([qs, ps, cbs]) => {
        if (cancelled) return;
        setQuotes(qs);
        setOrders(ps);
        setCallbackMcs(cbs);
        setNowMs(Date.now());
      })
      .catch((e) => {
        if (!cancelled) setError(e as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, role]);

  const data = useMemo<DashboardData>(() => buildDashboard(quotes, orders), [quotes, orders]);

  // A customer said yes without paying, or asked for a fresh quotation from
  // the expired public page — both are flags nobody else reads (payment-gated
  // confirmation slice 3). Deliberately the NARROWED `quotes` (this rep's own,
  // via the `own` scope above) — the dashboard is "my work"; this card is no
  // exception.
  const acceptedQuoteRows = useMemo<AcceptedQuoteRow[]>(
    () => (nowMs === 0 ? [] : buildAcceptedQuoteRows(quotes, nowMs)),
    [quotes, nowMs],
  );

  const [openWoOrderIds, setOpenWoOrderIds] = useState<Set<string>>(new Set());
  const [unpaidPrOrderIds, setUnpaidPrOrderIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void listWorkOrders()
      .then((ws) => {
        if (cancelled) return;
        setOpenWoOrderIds(
          new Set(ws.filter((w) => isOpenWorkOrder(w.status)).map((w) => w.orderId)),
        );
      })
      .catch(() => {
        /* non-fatal — the KPI just shows fewer */
      });
    void listRequisitions()
      .then((rs) => {
        if (cancelled) return;
        const unpaid = rs.filter((r) => r.status !== 'PAID' && r.status !== 'CANCELLED');
        setUnpaidPrOrderIds(new Set(unpaid.map((r) => r.orderId).filter((x): x is string => !!x)));
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ordersNeedingFulfilment = useMemo(
    () => new Set([...openWoOrderIds, ...unpaidPrOrderIds]).size,
    [openWoOrderIds, unpaidPrOrderIds],
  );

  // Callback requests on this rep's own quotes — the rep is the one notified,
  // so the request belongs on their worklist. Manual MCs (no source quote)
  // never match and surface on the measurements list instead.
  const callbackRequests = useMemo(() => {
    const myQuoteIds = new Set(quotes.map((q) => q.id));
    return callbackMcs.filter((mc) => myQuoteIds.has(mc.sourceQuoteId));
  }, [quotes, callbackMcs]);

  const startNewQuote = () => {
    useQuoteStore.getState().resetWorkspace();
    navigate('/staff');
  };
  const discardWorkspace = () => {
    useQuoteStore.getState().resetWorkspace();
    setWorkspaceDismissed(true);
  };

  const firstName = (profile?.fullName ?? '').trim().split(/\s+/)[0];

  // Baseline STAFF (ADR 0005 Phase 0b) — claims-only role. The dashboard
  // doesn't show quote / order widgets they have no access to; until
  // Phase 1 ships /staff/claims, this is their landing surface.
  if (role === 'STAFF') {
    return (
      <div className="max-w-[800px] mx-auto px-6 md:px-10 py-8 w-full">
        <SectionHeader
          title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
          subtitle="Claims submission is on the way."
        />
        <Card className="p-8 mt-6 text-center">
          <div className="text-secondary text-base font-medium mb-2">
            Staff claims is launching soon.
          </div>
          <p className="text-sm text-outline leading-relaxed max-w-md mx-auto">
            You'll be able to submit out-of-pocket claims (medical, tools, meals, fuel) from here
            once the claims module ships. In the meantime, give your receipts to finance as you do
            today.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 w-full">
      <div className="mb-8">
        <SectionHeader
          title={firstName ? `Welcome back, ${firstName}` : 'My work'}
          subtitle="Your quotes, orders, and balances to collect."
          action={
            <Button
              variant="primary"
              onClick={startNewQuote}
              icon={<span className="material-symbols-outlined text-[18px]">add_circle</span>}
            >
              New quote
            </Button>
          }
        />
      </div>

      {loading ? (
        <div className="text-secondary text-sm uppercase tracking-wider">Loading…</div>
      ) : error ? (
        <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded">
          {error.message}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Ongoing quotes" value={data.ongoingQuotes.length} />
            <KpiCard label="Active orders" value={data.activeOrders.length} />
            <KpiCard
              label="Outstanding to collect"
              value={formatSgd(data.totalOutstanding)}
              sub={
                data.completedWithBalance.length > 0
                  ? `across ${data.completedWithBalance.length} order${
                      data.completedWithBalance.length === 1 ? '' : 's'
                    }`
                  : 'all delivered work paid'
              }
            />
            <KpiCard
              label="Orders needing fulfilment"
              value={ordersNeedingFulfilment}
              sub="open work orders or unpaid requisitions"
            />
          </div>

          {hasWorkspace && !workspaceDismissed && (
            <div className="mb-6 border border-amber-300 bg-amber-50 rounded-lg p-4 flex flex-wrap items-center gap-4">
              <span className="material-symbols-outlined text-amber-700">edit_note</span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold text-amber-900 text-sm">Quote in progress</p>
                <p className="text-xs text-amber-800">
                  {wsCustomerName.trim() || 'Untitled customer'} · {wsItemCount} line item
                  {wsItemCount === 1 ? '' : 's'} in your calculator workspace.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => void navigate('/staff')}
                  className="bg-amber-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded hover:bg-amber-700 transition-colors"
                >
                  Resume
                </button>
                <button
                  onClick={discardWorkspace}
                  className="text-amber-800 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded border border-amber-300 hover:bg-amber-100 transition-colors"
                >
                  Discard
                </button>
              </div>
            </div>
          )}

          {callbackRequests.length > 0 && (
            <section className="mb-6 border border-amber-300 rounded-lg bg-amber-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-amber-200 flex items-center gap-2">
                <span className="material-symbols-outlined text-[20px] text-amber-700">
                  phone_callback
                </span>
                <h2 className="font-bold text-amber-900">Measurement callback requests</h2>
                <span className="ml-auto text-sm font-bold text-amber-800">
                  {callbackRequests.length}
                </span>
              </div>
              <div className="p-3 space-y-2">
                {callbackRequests.map((mc) => (
                  <CallbackRow key={mc.id} mc={mc} />
                ))}
              </div>
            </section>
          )}

          {acceptedQuoteRows.length > 0 && (
            <section className="mb-6 border border-blue-300 rounded-lg bg-blue-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-blue-200 flex items-center gap-2">
                <span className="material-symbols-outlined text-[20px] text-blue-700">
                  campaign
                </span>
                <h2 className="font-bold text-blue-900">Customer follow-up needed</h2>
                <span className="ml-auto text-sm font-bold text-blue-800">
                  {acceptedQuoteRows.length}
                </span>
              </div>
              <div className="p-3 space-y-2">
                {acceptedQuoteRows.map((r) => (
                  <Link
                    key={r.quoteId}
                    to="/staff/quotes"
                    className="flex flex-wrap items-center justify-between gap-3 bg-surface border border-blue-200 rounded px-3 py-2 hover:border-blue-400 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-on-surface">{r.customerName}</div>
                      <div className="text-xs text-secondary font-mono">{r.displayRef}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-blue-700">
                        {formatSgd(r.grandTotal)}
                      </div>
                      <div className="text-xs text-blue-900">
                        {ACCEPTED_SIGNAL_LABEL[r.signal]}
                        {r.daysToExpiry !== null && (
                          <>
                            {' · '}
                            {r.daysToExpiry >= 0
                              ? `${r.daysToExpiry}d to expiry`
                              : `expired ${Math.abs(r.daysToExpiry)}d ago`}
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card
              title="Ongoing quotes"
              action={
                <span className="text-xs font-bold text-outline">{data.ongoingQuotes.length}</span>
              }
            >
              <div className="space-y-2">
                {data.ongoingQuotes.length === 0 ? (
                  <EmptyLine>No quotes in progress — start a new one.</EmptyLine>
                ) : (
                  data.ongoingQuotes.map((q) => <QuoteRow key={q.id} quote={q} />)
                )}
              </div>
            </Card>

            <Card
              title="Active orders"
              action={
                <span className="text-xs font-bold text-outline">{data.activeOrders.length}</span>
              }
            >
              <div className="space-y-2">
                {data.activeOrders.length === 0 ? (
                  <EmptyLine>No orders in progress.</EmptyLine>
                ) : (
                  data.activeOrders.map((p) => <OrderRow key={p.id} order={p} />)
                )}
              </div>
            </Card>
          </div>

          <div className="mt-6">
            <Card
              title={
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[20px] text-amber-700">
                    payments
                  </span>
                  Completed — balance outstanding
                </span>
              }
              action={
                data.completedWithBalance.length > 0 ? (
                  <span className="text-sm font-bold text-amber-800">
                    {formatSgd(data.totalOutstanding)} · {data.completedWithBalance.length} order
                    {data.completedWithBalance.length === 1 ? '' : 's'}
                  </span>
                ) : undefined
              }
            >
              <div className="space-y-2">
                {data.completedWithBalance.length === 0 ? (
                  <EmptyLine>Every delivered order is fully paid. Nothing to chase.</EmptyLine>
                ) : (
                  data.completedWithBalance.map((c) => (
                    <OutstandingRow key={c.order.id} entry={c} />
                  ))
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

const EmptyLine = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-outline px-1 py-3 text-center">{children}</p>
);

const QuoteRow = ({ quote }: { quote: QuoteListLike }) => {
  const meta = QUOTE_STATUS_META[quote.status] ?? {
    label: quote.status,
    dot: 'bg-outline',
    text: 'text-outline',
  };
  const age = daysSince(quote.metadata?.updatedAt ?? quote.metadata?.createdAt);
  const stale = age !== null && age >= STALE_DAYS;
  return (
    <Link
      to={`/quote/${quote.id}`}
      className="block border border-outline-variant rounded p-3 bg-background hover:border-primary transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-on-surface truncate">
          {quote.customer?.name || 'Unnamed customer'}
        </span>
        <span className="font-bold text-on-surface shrink-0">
          {formatSgd(quote.totals?.grandTotal ?? 0)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-1.5">
        <span className={`flex items-center gap-1.5 text-xs font-bold ${meta.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
        <span className={`text-[11px] ${stale ? 'text-amber-700 font-bold' : 'text-outline'}`}>
          {stale && '⚠ '}
          {ageLabel(age)}
        </span>
      </div>
    </Link>
  );
};

const OrderRow = ({ order }: { order: Order }) => (
  <Link
    to={`/staff/orders/${order.id}`}
    className="block border border-outline-variant rounded p-3 bg-background hover:border-primary transition-colors"
  >
    <div className="flex items-center justify-between gap-3">
      <span className="font-semibold text-on-surface truncate">
        {order.customer?.name || 'Unnamed customer'}
      </span>
      <OrderStatusBadge status={order.status} />
    </div>
    <div className="flex items-center justify-between gap-3 mt-1.5">
      <span className="font-mono text-[11px] text-secondary">
        {displayOrderNumber(order.orderNumber)}
      </span>
      {order.contractValue != null && (
        <span className="text-[11px] text-outline">{formatSgd(order.contractValue)}</span>
      )}
    </div>
  </Link>
);

const CallbackRow = ({ mc }: { mc: MeasurementConfirmation }) => {
  const age = daysSince(mc.customerCallbackRequestedAt);
  return (
    <Link
      to={`/staff/orders/${mc.orderId}/measurements/${mc.id}`}
      className="block border border-amber-200 rounded p-3 bg-white hover:border-amber-400 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-on-surface truncate">
          {mc.siteContact.name || 'Customer'}
        </span>
        <span className="font-mono text-[11px] text-secondary shrink-0">{mc.mcNumber}</span>
      </div>
      <div className="text-xs text-secondary mt-1 truncate">
        {mc.siteContact.addressLine || '—'}
      </div>
      {mc.customerCallbackNote && (
        <div className="text-xs text-amber-900 mt-1 italic">
          &ldquo;{mc.customerCallbackNote}&rdquo;
        </div>
      )}
      <div className="text-[11px] text-amber-700 font-bold mt-1">Requested {ageLabel(age)}</div>
    </Link>
  );
};

const OutstandingRow = ({ entry }: { entry: CompletedBalance }) => (
  <Link
    to={`/staff/orders/${entry.order.id}`}
    className="block border border-outline-variant rounded p-3 bg-background hover:border-primary transition-colors"
  >
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <div className="min-w-[160px]">
        <div className="font-semibold text-on-surface truncate">
          {entry.order.customer?.name || 'Unnamed customer'}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-mono text-[11px] text-secondary">
            {displayOrderNumber(entry.order.orderNumber)}
          </span>
          <OrderStatusBadge status={entry.order.status} />
        </div>
      </div>
      <div className="flex items-center gap-5 text-right">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-outline">Contract</div>
          <div className="text-sm text-on-surface">{formatSgd(entry.contract)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-outline">Paid</div>
          <div className="text-sm text-green-700">{formatSgd(entry.paid)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
            Outstanding
          </div>
          <div className="text-base font-bold text-amber-800">{formatSgd(entry.outstanding)}</div>
        </div>
      </div>
    </div>
  </Link>
);
